/**
 * File: assets/basari-product-variants.js
 * Purpose: Colour-swatch → variant sync (gallery, price, stock, URL, swatch state)
 * Sprint: 2 — Variant synchronization
 * Owner: Basari Theme / PDP
 * Dependencies: [data-basari-product], [data-basari-variants-json], existing data-basari-* hooks
 * Notes: No libraries. Scoped to Basari Product Main only.
 */
(function () {
  'use strict';

  var SELECTED_CLASS = 'basari-product-options__swatch--selected';
  var STOCK_CLASS_PREFIX = 'basari-product-meta__stock--';
  var THUMB_ACTIVE = 'basari-gallery__thumbnail--active';
  var THUMB_ACTIVE_JS = 'js-basari-gallery-thumbnail--active';
  var DEFAULT_LOW_STOCK = 5;

  function parseJsonScript(root) {
    var node = root.querySelector('[data-basari-variants-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function replaceToken(template, token, value) {
    if (!template) return '';
    return String(template).split(token).join(String(value));
  }

  function BasariProductVariants(root) {
    this.root = root;
    this.config = parseJsonScript(root);
    if (!this.config || !this.config.variants) return;

    this.variantsById = {};
    this.mediaById = {};
    this.strings = this.config.strings || {};
    this.lowStockThreshold =
      typeof this.config.lowStockThreshold === 'number'
        ? this.config.lowStockThreshold
        : DEFAULT_LOW_STOCK;

    this.indexData();
    this.cacheDom();

    this.currentVariantId = String(
      this.root.getAttribute('data-variant-id') || this.config.variantId || ''
    );

    this.onClick = this.handleClick.bind(this);
    this.onThumbClick = this.handleThumbClick.bind(this);

    if (this.cache.swatches.length) {
      this.bind();
    }
    this.bindGallery();
    this.syncInitialGallery();
  }

  BasariProductVariants.prototype.indexData = function () {
    var variants = this.config.variants || [];
    var media = this.config.media || [];
    var i;

    for (i = 0; i < variants.length; i += 1) {
      this.variantsById[String(variants[i].id)] = variants[i];
    }

    for (i = 0; i < media.length; i += 1) {
      this.mediaById[String(media[i].id)] = media[i];
    }
  };

  BasariProductVariants.prototype.cacheDom = function () {
    var root = this.root;

    this.cache = {
      options: root.querySelector('[data-basari-options]'),
      swatches: Array.prototype.slice.call(
        root.querySelectorAll('.js-basari-product-swatch[data-variant-id]')
      ),
      priceRoot: root.querySelector('[data-basari-product-price]'),
      priceCurrent: root.querySelector('[data-basari-product-price-current]'),
      priceCurrentValue: root.querySelector(
        '[data-basari-product-price-current] .basari-product-price__current-value'
      ),
      priceCompare: root.querySelector('[data-basari-product-price-compare]'),
      priceCompareValue: root.querySelector(
        '[data-basari-product-price-compare] .basari-product-price__compare-value'
      ),
      priceSavings: root.querySelector('[data-basari-product-price-savings]'),
      priceBadge: root.querySelector('[data-basari-product-price-badge]'),
      priceSaveText: root.querySelector('[data-basari-product-price-save-text]'),
      metaRoot: root.querySelector('[data-basari-product-meta]'),
      stock: root.querySelector('[data-basari-stock]'),
      stockLabel: root.querySelector('[data-basari-stock-label]'),
      gallery: root.querySelector('[data-basari-gallery]'),
      galleryMain: root.querySelector('[data-basari-gallery-main]'),
      galleryImage: root.querySelector('.js-basari-gallery-image'),
      galleryThumbsList: root.querySelector('[data-basari-gallery-thumbnails]'),
      galleryThumbs: Array.prototype.slice.call(
        root.querySelectorAll('[data-basari-gallery-thumbnail]')
      ),
      galleryThumbItems: Array.prototype.slice.call(
        root.querySelectorAll('[data-basari-gallery-thumb-item]')
      ),
      galleryThumbItemsDefault: Array.prototype.slice.call(
        root.querySelectorAll('[data-basari-gallery-thumb-item]')
      ),
      galleryCounterCurrent: root.querySelector('[data-basari-gallery-counter-current]'),
      galleryCounterTotal: root.querySelector('[data-basari-gallery-counter-total]')
    };
  };

  BasariProductVariants.prototype.bind = function () {
    var options = this.cache.options;
    if (!options) return;

    /* Native <button> already activates on Enter/Space via click */
    options.addEventListener('click', this.onClick);
  };

  BasariProductVariants.prototype.productHasVariantGalleries = function () {
    var variants = this.config.variants || [];
    var i;
    for (i = 0; i < variants.length; i += 1) {
      if (this.getValidMediaIds(variants[i]).length) return true;
    }
    return false;
  };

  BasariProductVariants.prototype.bindGallery = function () {
    var list = this.cache.galleryThumbsList;
    if (!list) return;
    list.addEventListener('click', this.onThumbClick);
  };

  BasariProductVariants.prototype.syncInitialGallery = function () {
    var variant = this.variantsById[this.currentVariantId];
    if (!variant || !this.productHasVariantGalleries()) return;
    this.updateGallery(variant);
  };

  BasariProductVariants.prototype.handleThumbClick = function (event) {
    var thumb;
    var item;
    var mediaId;
    var variant;
    var mediaIds;
    var i;
    var allowed = false;

    thumb = event.target && event.target.closest
      ? event.target.closest('[data-basari-gallery-thumbnail]')
      : null;
    if (!thumb || !this.cache.galleryThumbsList || !this.cache.galleryThumbsList.contains(thumb)) {
      return;
    }

    if (thumb.disabled || thumb.getAttribute('aria-hidden') === 'true') return;

    item = thumb.closest ? thumb.closest('[data-basari-gallery-thumb-item]') : null;
    if (item && (item.hidden || item.getAttribute('aria-hidden') === 'true')) return;

    mediaId = String(thumb.getAttribute('data-media-id') || '');
    if (!mediaId || !this.mediaById[mediaId]) return;

    variant = this.variantsById[this.currentVariantId];
    mediaIds = this.getValidMediaIds(variant);

    if (mediaIds.length) {
      for (i = 0; i < mediaIds.length; i += 1) {
        if (mediaIds[i] === mediaId) {
          allowed = true;
          break;
        }
      }
      if (!allowed) return;
    } else {
      mediaIds = null;
    }

    event.preventDefault();
    try {
      this.setActiveGalleryMedia(mediaId, mediaIds);
    } catch (error) {
      /* Keep PDP usable if thumbnail media sync fails */
    }
  };

  BasariProductVariants.prototype.handleClick = function (event) {
    var swatch = event.target.closest
      ? event.target.closest('.js-basari-product-swatch[data-variant-id]')
      : null;
    if (!swatch || !this.cache.options.contains(swatch)) return;

    event.preventDefault();
    this.selectVariant(swatch.getAttribute('data-variant-id'), swatch);
  };

  BasariProductVariants.prototype.selectVariant = function (variantId, swatch) {
    var id = String(variantId || '');
    var variant = this.variantsById[id];
    if (!variant) return;
    if (id === this.currentVariantId && swatch && swatch.classList.contains(SELECTED_CLASS)) {
      swatch.focus();
      return;
    }

    this.currentVariantId = id;
    this.root.setAttribute('data-variant-id', id);

    this.updateSwatches(swatch, variant);
    this.updatePrice(variant);
    this.updateStock(variant);
    this.updateGallery(variant);
    this.updateUrl(id);
    this.dispatchChange(variant);

    if (swatch) swatch.focus();
  };

  BasariProductVariants.prototype.updateSwatches = function (activeSwatch, variant) {
    var strings = this.strings;
    var swatches = this.cache.swatches;
    var i;
    var swatch;
    var value;
    var label;
    var unavailable;

    for (i = 0; i < swatches.length; i += 1) {
      swatch = swatches[i];
      value = swatch.getAttribute('data-option-value') || '';
      unavailable = swatch.getAttribute('aria-disabled') === 'true';
      label = value;

      if (swatch === activeSwatch || String(swatch.getAttribute('data-variant-id')) === String(variant.id)) {
        swatch.classList.add(SELECTED_CLASS);
        swatch.setAttribute('aria-current', 'true');
        swatch.setAttribute('aria-pressed', 'true');
        if (strings.selected) label = label + ' — ' + strings.selected;
      } else {
        swatch.classList.remove(SELECTED_CLASS);
        swatch.removeAttribute('aria-current');
        swatch.setAttribute('aria-pressed', 'false');
      }

      if (unavailable && strings.unavailable) {
        label = label + ' — ' + strings.unavailable;
      }

      swatch.setAttribute('aria-label', label);
    }

    if (this.cache.options) {
      this.cache.options.setAttribute('data-variant-id', String(variant.id));
    }
  };

  BasariProductVariants.prototype.updatePrice = function (variant) {
    var priceRoot = this.cache.priceRoot;
    var compareAt = variant.compareAtPrice;
    var price = variant.price;
    var onSale =
      compareAt != null && compareAt !== '' && Number(compareAt) > Number(price);
    var savingsAmount;
    var savingsPercent;

    if (priceRoot) {
      priceRoot.setAttribute('data-variant-id', String(variant.id));
      priceRoot.setAttribute('data-on-sale', onSale ? 'true' : 'false');
      priceRoot.classList.toggle('basari-product-price--on-sale', onSale);
      priceRoot.classList.toggle('basari-product-price--unavailable', !variant.available);
    }

    if (this.cache.priceCurrent) {
      this.cache.priceCurrent.setAttribute('data-price', String(price));
    }
    if (this.cache.priceCurrentValue) {
      this.cache.priceCurrentValue.textContent = variant.priceFormatted || '';
    }

    if (this.cache.priceCompare) {
      if (onSale) {
        this.cache.priceCompare.hidden = false;
        this.cache.priceCompare.removeAttribute('hidden');
        this.cache.priceCompare.setAttribute('data-compare-at-price', String(compareAt));
        if (this.cache.priceCompareValue) {
          this.cache.priceCompareValue.textContent = variant.compareAtPriceFormatted || '';
        }
      } else {
        this.cache.priceCompare.hidden = true;
        this.cache.priceCompare.setAttribute('hidden', '');
      }
    }

    if (this.cache.priceSavings) {
      if (onSale) {
        savingsAmount = Number(compareAt) - Number(price);
        savingsPercent = Math.round((savingsAmount * 100) / Number(compareAt));

        this.cache.priceSavings.hidden = false;
        this.cache.priceSavings.removeAttribute('hidden');
        this.cache.priceSavings.setAttribute('data-savings-amount', String(savingsAmount));
        this.cache.priceSavings.setAttribute('data-savings-percent', String(savingsPercent));

        if (this.cache.priceBadge && this.strings.percentOff) {
          this.cache.priceBadge.textContent = replaceToken(
            this.strings.percentOff,
            '__P__',
            savingsPercent
          );
        }

        if (this.cache.priceSaveText && this.strings.saveAmount) {
          this.cache.priceSaveText.textContent = replaceToken(
            this.strings.saveAmount,
            '__A__',
            variant.savingsAmountFormatted || ''
          );
        }
      } else {
        this.cache.priceSavings.hidden = true;
        this.cache.priceSavings.setAttribute('hidden', '');
      }
    }
  };

  BasariProductVariants.prototype.getStockStatus = function (variant) {
    var qty;
    if (!variant.available) return 'out';

    qty = Number(variant.inventoryQuantity);
    if (
      variant.inventoryManagement &&
      !isNaN(qty) &&
      qty > 0 &&
      qty <= this.lowStockThreshold &&
      variant.inventoryPolicy !== 'continue'
    ) {
      return 'low';
    }

    return 'in';
  };

  BasariProductVariants.prototype.updateStock = function (variant) {
    var stock = this.cache.stock;
    var status;
    var label;

    if (this.cache.metaRoot) {
      this.cache.metaRoot.setAttribute('data-variant-id', String(variant.id));
    }

    if (!stock) return;

    status = this.getStockStatus(variant);
    stock.classList.remove(
      STOCK_CLASS_PREFIX + 'in',
      STOCK_CLASS_PREFIX + 'out',
      STOCK_CLASS_PREFIX + 'low'
    );
    stock.classList.add(STOCK_CLASS_PREFIX + status);
    stock.setAttribute('data-stock-status', status);
    stock.setAttribute('data-inventory-quantity', String(variant.inventoryQuantity));
    stock.setAttribute(
      'data-inventory-management',
      variant.inventoryManagement == null ? '' : String(variant.inventoryManagement)
    );
    stock.setAttribute(
      'data-inventory-policy',
      variant.inventoryPolicy == null ? '' : String(variant.inventoryPolicy)
    );

    if (this.cache.stockLabel) {
      if (status === 'low') label = this.strings.stockLow;
      else if (status === 'out') label = this.strings.stockOut;
      else label = this.strings.stockIn;
      if (label) this.cache.stockLabel.textContent = label;
    }
  };

  BasariProductVariants.prototype.getValidMediaIds = function (variant) {
    var raw;
    var result = [];
    var seen = {};
    var i;
    var id;

    if (!variant || !Array.isArray(variant.mediaIds) || !variant.mediaIds.length) return result;

    raw = variant.mediaIds;
    for (i = 0; i < raw.length; i += 1) {
      id = String(raw[i] == null ? '' : raw[i]);
      if (!id || seen[id] || !this.mediaById[id]) continue;
      seen[id] = true;
      result.push(id);
    }
    return result;
  };

  BasariProductVariants.prototype.resolveGalleryMediaId = function (variant, mediaIds) {
    var featured = variant && variant.featuredMediaId != null ? String(variant.featuredMediaId) : '';
    var i;
    if (mediaIds && mediaIds.length) {
      if (featured) {
        for (i = 0; i < mediaIds.length; i += 1) {
          if (mediaIds[i] === featured) return featured;
        }
      }
      return mediaIds[0];
    }
    return featured;
  };

  BasariProductVariants.prototype.setThumbItemHidden = function (item, hidden) {
    var thumb;
    if (!item) return;
    thumb = item.querySelector('[data-basari-gallery-thumbnail]');
    if (hidden) {
      item.hidden = true;
      item.setAttribute('hidden', '');
      item.setAttribute('aria-hidden', 'true');
      if (thumb) {
        thumb.setAttribute('tabindex', '-1');
        thumb.setAttribute('aria-hidden', 'true');
        if (thumb.tagName === 'BUTTON') thumb.disabled = true;
      }
    } else {
      item.hidden = false;
      item.removeAttribute('hidden');
      item.removeAttribute('aria-hidden');
      if (thumb) {
        thumb.removeAttribute('tabindex');
        thumb.removeAttribute('aria-hidden');
        if (thumb.tagName === 'BUTTON') thumb.disabled = false;
      }
    }
  };

  BasariProductVariants.prototype.applyMediaToImage = function (image, media) {
    if (!image || !media) return;

    if (media.src) image.setAttribute('src', media.src);
    if (media.srcset) {
      image.setAttribute('srcset', media.srcset);
    } else if (media.src) {
      image.removeAttribute('srcset');
    }
    if (media.sizes) image.setAttribute('sizes', media.sizes);
    if (media.alt != null) image.setAttribute('alt', media.alt);
    if (media.width) image.setAttribute('width', String(media.width));
    if (media.height) image.setAttribute('height', String(media.height));
  };

  BasariProductVariants.prototype.setActiveGalleryMedia = function (mediaId, mediaIds) {
    var media = mediaId ? this.mediaById[mediaId] : null;
    var gallery = this.cache.gallery;
    var main = this.cache.galleryMain;
    var image = this.cache.galleryImage;
    var thumbs = this.cache.galleryThumbs;
    var items = this.cache.galleryThumbItems;
    var i;
    var thumb;
    var item;
    var isActive;
    var position = 1;
    var total;

    if (!media || !gallery) return;

    gallery.setAttribute('data-active-media-id', mediaId);

    if (main) {
      main.setAttribute('data-media-id', mediaId);
      main.setAttribute('data-active-media', '');
    }

    if (image) {
      this.applyMediaToImage(image, media);
    }

    if (mediaIds && mediaIds.length) {
      for (i = 0; i < mediaIds.length; i += 1) {
        if (mediaIds[i] === mediaId) {
          position = i + 1;
          break;
        }
      }
      total = mediaIds.length;
    }

    for (i = 0; i < thumbs.length; i += 1) {
      thumb = thumbs[i];
      isActive = String(thumb.getAttribute('data-media-id')) === mediaId;
      thumb.classList.toggle(THUMB_ACTIVE, isActive);
      thumb.classList.toggle(THUMB_ACTIVE_JS, isActive);

      if (isActive) {
        thumb.setAttribute('data-active-media', '');
        thumb.setAttribute('aria-current', 'true');
        if (!mediaIds || !mediaIds.length) {
          position = Number(thumb.getAttribute('data-media-position')) || i + 1;
        }
      } else {
        thumb.removeAttribute('data-active-media');
        thumb.removeAttribute('aria-current');
      }
    }

    for (i = 0; i < items.length; i += 1) {
      item = items[i];
      isActive = String(item.getAttribute('data-media-id')) === mediaId;
      if (isActive) item.setAttribute('data-active-media', '');
      else item.removeAttribute('data-active-media');
    }

    if (!mediaIds || !mediaIds.length) {
      total = this.cache.galleryThumbItemsDefault.length || items.length;
    }

    if (this.cache.galleryCounterCurrent) {
      this.cache.galleryCounterCurrent.textContent = String(position);
    }
    if (this.cache.galleryCounterTotal) {
      this.cache.galleryCounterTotal.textContent = String(total);
    }
  };

  BasariProductVariants.prototype.applyVariantGallery = function (variant, mediaIds) {
    var gallery = this.cache.gallery;
    var list = this.cache.galleryThumbsList;
    var items = this.cache.galleryThumbItems;
    var itemsById = {};
    var i;
    var item;
    var id;
    var mediaId;

    gallery.setAttribute('data-variant-id', String(variant.id));
    gallery.setAttribute('data-media-count', String(mediaIds.length));
    gallery.setAttribute('data-basari-variant-gallery-active', 'true');

    for (i = 0; i < items.length; i += 1) {
      item = items[i];
      id = String(item.getAttribute('data-media-id') || '');
      itemsById[id] = item;
      this.setThumbItemHidden(item, true);
    }

    if (list) {
      for (i = 0; i < mediaIds.length; i += 1) {
        item = itemsById[mediaIds[i]];
        if (!item) continue;
        this.setThumbItemHidden(item, false);
        item.style.order = String(i + 1);
        list.appendChild(item);
      }
    }

    mediaId = this.resolveGalleryMediaId(variant, mediaIds);
    this.setActiveGalleryMedia(mediaId, mediaIds);
  };

  BasariProductVariants.prototype.resetGalleryToDefault = function (variant) {
    var gallery = this.cache.gallery;
    var list = this.cache.galleryThumbsList;
    var originals = this.cache.galleryThumbItemsDefault;
    var i;
    var mediaId = variant.featuredMediaId != null ? String(variant.featuredMediaId) : '';

    gallery.removeAttribute('data-basari-variant-gallery-active');
    gallery.setAttribute('data-variant-id', String(variant.id));
    gallery.setAttribute('data-media-count', String(originals.length));

    if (list) {
      for (i = 0; i < originals.length; i += 1) {
        this.setThumbItemHidden(originals[i], false);
        originals[i].style.order = '';
        list.appendChild(originals[i]);
      }
    }

    this.setActiveGalleryMedia(mediaId, null);
  };

  BasariProductVariants.prototype.updateGalleryLegacy = function (variant) {
    var mediaId = variant.featuredMediaId != null ? String(variant.featuredMediaId) : '';
    var media = mediaId ? this.mediaById[mediaId] : null;
    var gallery = this.cache.gallery;
    var main = this.cache.galleryMain;
    var image = this.cache.galleryImage;
    var thumbs = this.cache.galleryThumbs;
    var items = this.cache.galleryThumbItems;
    var i;
    var thumb;
    var item;
    var isActive;
    var position = 1;

    if (!media || !gallery) return;

    gallery.setAttribute('data-variant-id', String(variant.id));
    gallery.setAttribute('data-active-media-id', mediaId);

    if (main) {
      main.setAttribute('data-media-id', mediaId);
      main.setAttribute('data-active-media', '');
    }

    if (image) {
      this.applyMediaToImage(image, media);
    }

    for (i = 0; i < thumbs.length; i += 1) {
      thumb = thumbs[i];
      isActive = String(thumb.getAttribute('data-media-id')) === mediaId;
      thumb.classList.toggle(THUMB_ACTIVE, isActive);
      thumb.classList.toggle(THUMB_ACTIVE_JS, isActive);

      if (isActive) {
        thumb.setAttribute('data-active-media', '');
        thumb.setAttribute('aria-current', 'true');
        position = Number(thumb.getAttribute('data-media-position')) || i + 1;
      } else {
        thumb.removeAttribute('data-active-media');
        thumb.removeAttribute('aria-current');
      }
    }

    for (i = 0; i < items.length; i += 1) {
      item = items[i];
      isActive = String(item.getAttribute('data-media-id')) === mediaId;
      if (isActive) item.setAttribute('data-active-media', '');
      else item.removeAttribute('data-active-media');
    }

    if (this.cache.galleryCounterCurrent) {
      this.cache.galleryCounterCurrent.textContent = String(position);
    }
  };

  BasariProductVariants.prototype.updateGallery = function (variant) {
    var gallery = this.cache.gallery;
    var mediaIds;

    try {
      if (!gallery || !variant) return;
      mediaIds = this.getValidMediaIds(variant);
      if (mediaIds.length) {
        this.applyVariantGallery(variant, mediaIds);
        return;
      }
      if (this.productHasVariantGalleries()) {
        this.resetGalleryToDefault(variant);
        return;
      }
      this.updateGalleryLegacy(variant);
    } catch (error) {
      /* Keep PDP usable if gallery sync fails */
    }
  };

  BasariProductVariants.prototype.updateUrl = function (variantId) {
    var url;
    if (!window.history || !window.history.replaceState) return;

    try {
      url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      /* Older browsers / invalid URL — no-op */
    }
  };

  BasariProductVariants.prototype.dispatchChange = function (variant) {
    var event;
    try {
      event = new CustomEvent('basari:variant:change', {
        bubbles: true,
        detail: {
          variant: variant,
          productId: this.config.productId,
          sectionId: this.root.getAttribute('data-section-id')
        }
      });
      this.root.dispatchEvent(event);
    } catch (error) {
      /* IE / custom event unsupported — ignore */
    }
  };

  function initRoot(root) {
    if (!root || root.getAttribute('data-basari-variants-ready') === 'true') return;
    root.setAttribute('data-basari-variants-ready', 'true');
    new BasariProductVariants(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll
      ? (scope || document).querySelectorAll('[data-basari-product]')
      : [];
    var i;
    for (i = 0; i < roots.length; i += 1) {
      initRoot(roots[i]);
    }
  }

  function onSectionLoad(event) {
    var section = event.target;
    if (!section || !section.querySelector) return;
    if (!section.querySelector('[data-basari-product]')) return;
    initAll(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
})();
